// Admin — Drivers list (Wave E).

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, Pencil, Plus, Trash2, Truck } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { FilterBar } from '../../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../../components/ui/Table/Table';
import { Pagination } from '../../../components/ui/Pagination/Pagination';
import { Badge } from '../../../components/ui/Badge/Badge';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { Select } from '../../../components/form/Select';
import { useDeleteDriver, useDriversList } from './hooks/useDriversAdmin';
import type { DriverSummary } from '../../../types/api';
import styles from './DriversListPage.module.css';

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function DriversListPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const status = (params.get('status') ?? 'all') as 'active' | 'deactivated' | 'all';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const list = useDriversList({ page, status, q: q || undefined });
  const deleteMutation = useDeleteDriver();

  const [deleteTarget, setDeleteTarget] = useState<DriverSummary | null>(null);

  const drivers = list.data?.drivers ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const columns: Column<DriverSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (_v, row) => (
          <button
            type="button"
            className={styles.refLink}
            onClick={() =>
              navigate(`/admin/drivers/${encodeURIComponent(row.uuid)}`)
            }
          >
            {row.name}
          </button>
        ),
      },
      { key: 'username', header: 'Username' },
      { key: 'phone', header: 'Phone' },
      {
        key: 'email',
        header: 'Email',
        render: (_v, row) => row.email ?? '—',
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) =>
          row.status === 'active' ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="muted">Deactivated</Badge>
          ),
      },
      {
        key: 'last_login_at',
        header: 'Last Login',
        render: (_v, row) => formatDate(row.last_login_at),
      },
    ],
    [navigate],
  );

  const rowActions = (row: DriverSummary): RowAction<DriverSummary>[] => [
    {
      label: 'View',
      icon: Eye,
      onClick: () =>
        navigate(`/admin/drivers/${encodeURIComponent(row.uuid)}`),
      variant: 'ghost',
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () =>
        navigate(`/admin/drivers/${encodeURIComponent(row.uuid)}/edit`),
      variant: 'ghost',
    },
    {
      label: 'Deactivate',
      icon: Trash2,
      onClick: () => setDeleteTarget(row),
      variant: 'danger',
      hidden: (r) => r.status === 'deactivated',
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Driver Management"
        subtitle={`${total} driver${total === 1 ? '' : 's'}`}
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/admin/drivers/create')}
          >
            Add Driver
          </Button>
        }
      />

      <Card flush>
        <FilterBar
          search={{
            value: q,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by name, phone, or username…',
            ariaLabel: 'Search drivers',
          }}
        >
          <Select
            aria-label="Status filter"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              { value: 'deactivated', label: 'Deactivated' },
            ]}
          />
        </FilterBar>

        <Table
          columns={columns}
          data={drivers as DriverSummary[]}
          keyField={(row) => row.uuid}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <Truck size={28} aria-hidden />
              <h3>No drivers found</h3>
              <p>Adjust your filters or add a new driver.</p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Drivers"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Deactivate driver?"
        message={
          deleteTarget !== null
            ? `Deactivate ${deleteTarget.name}? They will lose Dispatch access immediately. The record is preserved.`
            : ''
        }
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget === null) return;
          deleteMutation.mutate(deleteTarget.uuid, {
            onSuccess: () => {
              setDeleteTarget(null);
              void list.refetch();
            },
          });
        }}
      />
    </div>
  );
}
